/**
 * Treasury Dashboard Deep-Dive
 * BTC lock management, hotel treasury balances, protocol pools, transaction history
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowLeft, ArrowUp, Bitcoin, Building2,
  CircleDollarSign, Clock, Coins, Lock, RefreshCw,
  Search, Shield, ShieldAlert, Sparkles, Timer,
  TrendingUp, Wallet,
} from 'lucide-react';
import {
  getBtcTreasury, getProtocolPools, getHotelTreasury,
  getTreasuryTransactions,
} from '@/lib/v2Api';
import TerminalFloorNav from '../components/TerminalFloorNav';

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

// ─── BTC Treasury Card ───
const BtcTreasuryCard = ({ btc }) => {
  const isLocked = btc.status === 'locked';
  const lockEnd = btc.lock_end ? new Date(btc.lock_end) : null;
  const now = new Date();
  const daysLeft = lockEnd ? Math.max(0, Math.ceil((lockEnd - now) / (1000 * 60 * 60 * 24))) : 0;
  const yearsLeft = lockEnd ? (daysLeft / 365).toFixed(1) : '—';
  const lockStart = btc.lock_start ? new Date(btc.lock_start) : null;
  const totalDays = lockStart && lockEnd ? Math.ceil((lockEnd - lockStart) / (1000 * 60 * 60 * 24)) : 1095;
  const progressPct = totalDays > 0 ? Math.min(100, ((totalDays - daysLeft) / totalDays) * 100) : 0;

  return (
    <Card className="relative overflow-hidden border-[hsl(var(--web3-accent))]/20" data-testid="btc-treasury-card">
      <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_80%_20%,rgba(176,141,87,0.06)_0%,transparent_60%)]" />
      <CardHeader className="relative pb-3">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Bitcoin className="h-5 w-5 text-[hsl(var(--web3-accent))]" />
          BTC Treasury
        </CardTitle>
        <CardDescription>1 BTC sovereign reserve — "The Land" of the protocol</CardDescription>
      </CardHeader>
      <CardContent className="relative space-y-5">
        {/* Main Value */}
        <div className="text-center py-3">
          <p className="text-5xl heading-font font-black tracking-tight">{btc.amount_btc} BTC</p>
          <Badge variant={isLocked ? 'default' : 'secondary'} className="mt-3 text-xs">
            {isLocked ? (
              <><Lock className="h-3 w-3 mr-1" /> Locked</>
            ) : (
              <><Clock className="h-3 w-3 mr-1" /> Pending Lock</>
            )}
          </Badge>
        </div>

        {isLocked ? (
          <>
            {/* Countdown */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Time remaining</span>
                <span className="text-xs heading-font tabular-nums">{progressPct.toFixed(0)}% elapsed</span>
              </div>
              <Progress value={progressPct} className="h-2.5 [&>div]:bg-[hsl(var(--web3-accent))]" />
              <div className="grid grid-cols-3 gap-3 text-center">
                <div data-testid="btc-days-left">
                  <p className="text-2xl heading-font font-bold tabular-nums">{fmt(daysLeft)}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Days Left</p>
                </div>
                <div>
                  <p className="text-2xl heading-font font-bold tabular-nums">{yearsLeft}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Years Left</p>
                </div>
                <div>
                  <p className="text-2xl heading-font font-bold tabular-nums">{btc.lock_extensions?.length || 0}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Extensions</p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lock Start</span>
                <span className="tabular-nums">{lockStart?.toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lock End</span>
                <span className="tabular-nums">{lockEnd?.toLocaleDateString()}</span>
              </div>
              {btc.mpc_wallet_address && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">MPC Wallet</span>
                  <span className="font-mono text-xs truncate max-w-[180px]">{btc.mpc_wallet_address}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-4 space-y-2">
            <Timer className="h-8 w-8 text-muted-foreground/40 mx-auto" />
            <p className="text-sm text-muted-foreground">BTC not yet locked. Awaiting Fystack MPC wallet setup.</p>
            <p className="text-xs text-muted-foreground">3-of-3 consensus required for any movement.</p>
          </div>
        )}

        {/* Extensions */}
        {btc.lock_extensions?.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Lock Extensions</p>
              {btc.lock_extensions.map((ext, i) => (
                <div key={i} className="flex justify-between text-xs py-1.5">
                  <span className="text-muted-foreground">{ext.reason}</span>
                  <span className="heading-font font-medium">+{ext.months}mo</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Protocol Pool Card ───
const ProtocolPoolCard = ({ pool, icon: Icon, description }) => {
  const inflowPct = pool.total_inflows > 0
    ? ((pool.total_outflows / pool.total_inflows) * 100).toFixed(0)
    : 0;

  return (
    <Card className="card-hover-lift" data-testid={`pool-${pool.pool_type}`}>
      <CardContent className="pt-5 pb-4 space-y-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              {pool.pool_type.replace(/_/g, ' ')}
            </p>
            <p className="text-2xl heading-font tabular-nums font-semibold">{fmt(pool.balance)}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          <div className="h-10 w-10 rounded-lg bg-[hsl(var(--web3-primary))]/10 flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
          </div>
        </div>
        <Separator />
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-1.5">
            <ArrowDown className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-muted-foreground text-xs">In:</span>
            <span className="tabular-nums font-medium text-xs">{fmt(pool.total_inflows)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ArrowUp className="h-3.5 w-3.5 text-red-500" />
            <span className="text-muted-foreground text-xs">Out:</span>
            <span className="tabular-nums font-medium text-xs">{fmt(pool.total_outflows)}</span>
          </div>
        </div>
        {pool.last_updated && (
          <p className="text-[10px] text-muted-foreground">
            Updated: {new Date(pool.last_updated).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Hotel Treasury View ───
const HotelTreasuryView = ({ treasury, loading }) => {
  if (loading) return <TreasurySkeleton />;
  if (!treasury) return (
    <div className="flex flex-col items-center justify-center py-16 text-center space-y-3">
      <Search className="h-12 w-12 text-muted-foreground/40" />
      <p className="text-lg heading-font text-muted-foreground">Enter a Hotel ID to view its treasury</p>
    </div>
  );
  if (treasury.error) return (
    <div className="text-center py-16">
      <p className="text-sm text-red-400">{treasury.error}</p>
    </div>
  );

  const balances = [
    { label: 'Sage Treasury', value: treasury.sage_treasury_balance, icon: CircleDollarSign, color: 'text-emerald-500', desc: 'Hotel operations fund' },
    { label: 'Bitsy Wallet', value: treasury.bitsy_wallet_balance, icon: Sparkles, color: 'text-amber-500', desc: 'AI concierge operational fund' },
    { label: 'Staked', value: treasury.staked_amount, icon: Lock, color: 'text-violet-500', desc: 'Slash-only staking' },
    { label: 'Sage Buffer', value: treasury.sage_buffer_balance, icon: Shield, color: 'text-teal-500', desc: '1% operational buffer' },
  ];

  const metrics = [
    { label: 'Booking Revenue', value: treasury.total_booking_revenue },
    { label: 'Staff Bonuses Paid', value: treasury.total_staff_bonuses_paid },
    { label: 'Guest Rewards Paid', value: treasury.total_guest_rewards_paid },
    { label: 'Bitsy Refills', value: treasury.total_bitsy_refills },
    { label: 'Emergency Fund Contribution', value: treasury.emergency_fund_contribution },
  ];

  const totalBalance = balances.reduce((sum, b) => sum + (b.value || 0), 0);

  return (
    <div className="space-y-5" data-testid="hotel-treasury-view">
      {/* Total */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-[hsl(var(--web3-surface-2))]/60 border">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Hotel Assets</p>
          <p className="text-3xl heading-font font-bold tabular-nums mt-1">{fmt(totalBalance)} <span className="text-base text-muted-foreground font-normal">HERMES</span></p>
        </div>
        <Building2 className="h-8 w-8 text-muted-foreground/30" />
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {balances.map(({ label, value, icon: Icon, color, desc }) => (
          <Card key={label} className="card-hover-lift" data-testid={`hotel-balance-${label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
                  <p className="text-2xl heading-font tabular-nums font-semibold">{fmt(value)}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
                <Icon className={`h-5 w-5 ${color} shrink-0`} />
              </div>
              {totalBalance > 0 && (
                <div className="mt-3">
                  <Progress value={(value / totalBalance) * 100} className="h-1.5" />
                  <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                    {((value / totalBalance) * 100).toFixed(1)}% of total
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Operational Metrics */}
      <Card data-testid="hotel-operational-metrics">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg heading-font flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
            Operational Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-6">
            {metrics.map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">{label}</span>
                <span className="text-sm heading-font tabular-nums font-medium">{fmt(value)} <span className="text-xs text-muted-foreground">HERMES</span></span>
              </div>
            ))}
          </div>
          {treasury.created_at && (
            <p className="text-[10px] text-muted-foreground mt-3 pt-3 border-t">
              Treasury created: {new Date(treasury.created_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Transaction History ───
const TransactionHistory = ({ transactions, loading }) => (
  <Card data-testid="transaction-history-card">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg heading-font flex items-center gap-2">
        <Wallet className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
        Transaction History
      </CardTitle>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <ScrollArea className="h-[320px]">
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {transactions.map((tx, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${
                      tx.direction === 'inflow' ? 'bg-emerald-500/10' : 'bg-red-500/10'
                    }`}>
                      {tx.direction === 'inflow'
                        ? <ArrowDown className="h-3.5 w-3.5 text-emerald-500" />
                        : <ArrowUp className="h-3.5 w-3.5 text-red-500" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate max-w-[300px]">{tx.reason}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {tx.pool?.replace(/_/g, ' ')} {tx.hotel_id ? `| ${tx.hotel_id}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    <p className={`text-sm heading-font tabular-nums font-medium ${
                      tx.direction === 'inflow' ? 'text-emerald-500' : 'text-red-500'
                    }`}>
                      {tx.direction === 'inflow' ? '+' : '-'}{fmt(tx.amount)}
                    </p>
                    <p className="text-[10px] text-muted-foreground tabular-nums">
                      {new Date(tx.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      )}
    </CardContent>
  </Card>
);

// ─── Skeleton ───
const TreasurySkeleton = () => (
  <div className="space-y-5">
    <Skeleton className="h-20 w-full" />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[1,2,3,4].map(i => <Card key={i}><CardContent className="pt-5 pb-4 space-y-3"><Skeleton className="h-3 w-24" /><Skeleton className="h-8 w-32" /></CardContent></Card>)}
    </div>
  </div>
);

// ─── Main Page ───
export default function TreasuryDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('protocol');
  const [btc, setBtc] = useState(null);
  const [pools, setPools] = useState([]);
  const [hotelTreasury, setHotelTreasury] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [hotelId, setHotelId] = useState('');
  const [loading, setLoading] = useState(true);
  const [hotelLoading, setHotelLoading] = useState(false);
  const [txLoading, setTxLoading] = useState(false);

  const poolConfigs = {
    protocol_staking_reward: { icon: Coins, desc: 'Funded by 1% of every booking + slash proceeds' },
    whistleblower: { icon: Shield, desc: '50% of slashed stakes for whistleblower rewards' },
    emergency_mutual_fund: { icon: ShieldAlert, desc: '0.5% of Sage treasury contributions' },
  };

  const loadProtocol = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p, t] = await Promise.all([
        getBtcTreasury(),
        getProtocolPools(),
        getTreasuryTransactions({ limit: 50 }),
      ]);
      setBtc(b);
      setPools(p.pools || []);
      setTransactions(t.transactions || []);
    } catch { toast.error('Failed to load treasury data'); }
    finally { setLoading(false); }
  }, []);

  const loadHotel = useCallback(async (id) => {
    if (!id) return;
    setHotelLoading(true);
    setTxLoading(true);
    try {
      const [h, t] = await Promise.all([
        getHotelTreasury(id).catch(() => ({ error: `Hotel "${id}" not found` })),
        getTreasuryTransactions({ hotel_id: id, limit: 50 }),
      ]);
      setHotelTreasury(h);
      setTransactions(t.transactions || []);
      setHotelId(id);
    } catch { toast.error('Failed to load hotel treasury'); }
    finally { setHotelLoading(false); setTxLoading(false); }
  }, []);

  useEffect(() => { loadProtocol(); }, [loadProtocol]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) loadHotel(searchInput.trim());
  };

  return (
    <div className="terminal-theme min-h-dvh">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl heading-font font-semibold tracking-tight" data-testid="page-title">Treasury</h1>
              <p className="text-xs text-muted-foreground">BTC Reserve, Protocol Pools & Hotel Treasuries</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadProtocol(); if (hotelId) loadHotel(hotelId); }} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
            <TabsList data-testid="treasury-tabs">
              <TabsTrigger value="protocol" data-testid="tab-protocol">
                <Coins className="h-4 w-4 mr-2" /> Protocol
              </TabsTrigger>
              <TabsTrigger value="hotel" data-testid="tab-hotel">
                <Building2 className="h-4 w-4 mr-2" /> Hotel
              </TabsTrigger>
            </TabsList>

            {activeTab === 'hotel' && (
              <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
                <Input
                  placeholder="Enter Hotel ID (e.g. sunrise-beach-resort-ff14)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="text-sm"
                  data-testid="hotel-id-input"
                />
                <Button type="submit" size="sm" variant="outline" data-testid="search-btn">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            )}
          </div>

          <TabsContent value="protocol">
            {loading ? <TreasurySkeleton /> : (
              <div className="space-y-6">
                {/* BTC + Pools */}
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                  <div className="lg:col-span-5">
                    {btc && <BtcTreasuryCard btc={btc} />}
                  </div>
                  <div className="lg:col-span-7 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {pools.map((pool) => {
                        const cfg = poolConfigs[pool.pool_type] || { icon: Coins, desc: '' };
                        return <ProtocolPoolCard key={pool.pool_type} pool={pool} icon={cfg.icon} description={cfg.desc} />;
                      })}
                    </div>
                    {/* Protocol Transactions */}
                    <TransactionHistory transactions={transactions} loading={false} />
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="hotel">
            <div className="space-y-6">
              <HotelTreasuryView treasury={hotelTreasury} loading={hotelLoading} />
              {hotelId && <TransactionHistory transactions={transactions} loading={txLoading} />}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <TerminalFloorNav />
    </div>
  );
}
